import {
    ArrowLeftOutlined,
    EditOutlined,
    ReadOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCaseDocument } from '../api';
import DocOutline, { useDocOutline } from '../components/DocOutline';
import PreviewMarkdown from '../StepForm/PreviewStep/PreviewMarkdown';
import styles from './index.module.scss';

const formatWords = (n) => {
    if (!n) return '';
    if (n >= 10000) return `${(n / 10000).toFixed(1).replace(/\.0$/, '')} 万`;
    return String(n);
};

/** 案例库范文阅读页：/case/:id，只读，无需登录，左侧带正文目录 */
export default function CaseReader() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tocOpen, setTocOpen] = useState(false);
    const bodyRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getCaseDocument(id);
            setData(res || null);
        } catch (err) {
            const text = err?.message || '';
            setError(/[一-龥]/.test(text) ? text : '范文加载失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
        window.scrollTo({ top: 0 });
    }, [load]);

    const title = data?.title || '范文阅读';
    const content = data?.content || '';
    const ready = !(loading && !data) && !error;
    // 目录从渲染出来的正文标题收集，正文变了重新收集
    const outline = useDocOutline(bodyRef, [content, ready]);

    useEffect(() => {
        if (!data?.title) return undefined;
        const prev = document.title;
        document.title = `${data.title} - 范文 - 灵感风暴`;
        return () => {
            document.title = prev;
        };
    }, [data?.title]);

    const backToLibrary = () => navigate('/#case-library');
    const startWriting = () => navigate('/');

    const handleOutlineSelect = (outlineId) => {
        setTocOpen(false);
        outline.scrollTo(outlineId);
    };

    const meta = data
        ? [
              data.categoryLabel,
              data.wordCount ? `约 ${formatWords(data.wordCount)} 字` : '',
              ...(Array.isArray(data.tags) ? data.tags : []),
          ].filter(Boolean)
        : [];

    return (
        <div className={styles.page}>
            <header className={styles.bar}>
                <div className={styles.barLeft}>
                    <button type="button" className={styles.backBtn} onClick={backToLibrary}>
                        <ArrowLeftOutlined />
                        <span className={styles.backText}>返回案例库</span>
                    </button>
                    <span className={styles.tag}>
                        <ReadOutlined /> 范文 · 只读
                    </span>
                    <h1 className={styles.barTitle} title={title}>
                        {title}
                    </h1>
                </div>
                <div className={styles.barRight}>
                    <Button
                        icon={<UnorderedListOutlined />}
                        className={styles.tocBtn}
                        onClick={() => setTocOpen(true)}
                        disabled={!ready}
                        aria-label="打开目录"
                    >
                        目录
                    </Button>
                    <Button type="primary" icon={<EditOutlined />} onClick={startWriting}>
                        写一篇同款
                    </Button>
                </div>
            </header>

            <main className={styles.scroll}>
                {loading && !data ? (
                    <div className={styles.state}>
                        <Spin /> 正在加载范文…
                    </div>
                ) : error ? (
                    <div className={styles.state}>
                        <p className={styles.error}>{error}</p>
                        <div className={styles.stateActions}>
                            <Button icon={<ReloadOutlined />} onClick={load}>
                                重试
                            </Button>
                            <Button type="primary" onClick={backToLibrary}>
                                返回案例库
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className={styles.workspace}>
                        <aside className={styles.side}>
                            <DocOutline items={outline.items} activeId={outline.activeId} onSelect={outline.scrollTo} />
                        </aside>
                        <article className={styles.paper}>
                            <h1 className={styles.paperTitle}>{title}</h1>
                            {meta.length > 0 && (
                                <div className={styles.meta}>
                                    {meta.map((m) => (
                                        <span key={m} className={styles.metaItem}>
                                            {m}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div ref={bodyRef}>
                                <PreviewMarkdown content={content} rawHtml placeholderText="图片暂缺" />
                            </div>
                            <div className={styles.foot}>
                                范文由灵感风暴生成，仅供参考与学习，请勿直接提交
                            </div>
                        </article>
                    </div>
                )}
            </main>

            <Drawer
                open={tocOpen}
                onClose={() => setTocOpen(false)}
                placement="left"
                width="min(86vw, 320px)"
                title="目录"
                rootClassName={styles.drawer}
            >
                <DocOutline items={outline.items} activeId={outline.activeId} onSelect={handleOutlineSelect} />
            </Drawer>
        </div>
    );
}
